import {
  Accessor,
  createEffect,
  createRoot,
  getOwner,
  on,
  onCleanup,
  runWithOwner,
} from 'solid-js';
import { useLogger } from '../providers/LoggerProvider';
import { asError } from '@trimble/logging';

/**
 * Creates an effect that runs async callbacks sequentially.
 * Each new execution waits for the previous one to complete before starting.
 * Similar to RxJS `concatMap` behavior.
 *
 * Each invocation runs inside its own reactive scope ({@link createRoot}).
 * When the source changes, the previous invocation's scope is automatically
 * disposed, cleaning up any reactive primitives (effects, memos) created
 * inside. The callback can call `getOwner()` to capture the per-invocation
 * root and use `runWithOwner` after `await` to create properly scoped effects.
 *
 * @param source - Reactive accessor that triggers the effect
 * @param fn - Async function to execute when source changes
 * @param options - Optional configuration (e.g., defer initial execution)
 * @param options.defer - If true, skip the initial execution
 * @param options.equals - Optional equality function. When provided, if
 *   `equals(value, prevValue)` returns `true`, the invocation is skipped
 *   entirely -- no scope disposal, no new scope, no callback. Use this
 *   when the source signal may fire with structurally equal but
 *   reference-different values to prevent accidental scope destruction.
 *
 * @example
 * ```ts
 * createSequentialEffect(
 *   () => viewMode(),
 *   async (vm) => {
 *     const owner = getOwner(); // per-invocation root
 *     await teardown();
 *     runWithOwner(owner, () => {
 *       // Effects here are auto-disposed when source changes again
 *       createEffect(() => { ... });
 *     });
 *   },
 *   { equals: viewModeEquals }
 * );
 * ```
 */
export function createSequentialEffect<T>(
  source: Accessor<T>,
  fn: (value: T, prevValue: T | undefined) => Promise<void>,
  options?: {
    defer?: boolean;
    equals?(value: NoInfer<T>, prevValue: NoInfer<T> | undefined): boolean;
  }
): void {
  let queue: Promise<void> = Promise.resolve();
  let disposed = false;
  let disposePreviousRun: (() => void) | undefined;
  const owner = getOwner();
  const logger = useLogger().getLogger('createSequentialEffect');

  createEffect(
    on(
      source,
      (value, prevValue) => {
        queue = queue
          .then(async () => {
            if (disposed) return;
            if (options?.equals?.(value, prevValue)) return;
            disposePreviousRun?.();
            if (owner) {
              await new Promise<void>((resolve, reject) => {
                runWithOwner(owner, () => {
                  // eslint-disable-next-line sonarjs/no-nested-functions -- createRoot callback is the natural shape for scoped disposal
                  createRoot((dispose) => {
                    disposePreviousRun = dispose;
                    fn(value, prevValue).then(resolve).catch(reject);
                  });
                });
              });
            } else {
              await fn(value, prevValue);
            }
          })
          .catch((error: unknown) => {
            logger.error(
              'Error in sequential effect',
              {
                value,
                prevValue,
              },
              asError(error)
            );
          });
      },
      { defer: options?.defer }
    )
  );

  onCleanup(() => {
    disposed = true;
    disposePreviousRun?.();
  });
}
